import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import {
  Popup,
  Icon,
  Form,
  Input,
  Header,
  Dropdown,
  Divider,
} from 'semantic-ui-react';
import cn from 'classnames';

import { CacheInputs, Planning, Portfolio, Accounts } from 'actions';
import { colors } from 'utils/colors';
import { convertToTickersAndWeights } from 'utils/helpers';
import {
  Message,
  ErrorLabel,
  Button,
  Segment,
  PortfolioAccountSelector,
} from 'components';
import Result from './Result';

@connect((state) => ({
  isFetching: state.Planning.isFetching,
  longevity: state.Planning.longevity,
  portfolios: state.Portfolio.model.concat(state.Portfolio.user),
  portLongevityInputs: state.CacheInputs.planning.portfolio_longevity,
  accountData: state.Accounts.accountData,
  accountList: state.Accounts.accountList,
}))
export default class Longevity extends Component {
  componentWillMount() {
    const { dispatch, portfolios } = this.props;

    dispatch(Accounts.fetchAll());
    dispatch(Accounts.fetchAllData());

    if (!portfolios.length) {
      dispatch(Portfolio.fetchUser());
      dispatch(Portfolio.fetchModel());
    }

    return false;
  }

  getPortDate = (port) => {
    if (port && !port.init_date) {
      return false;
    }

    const date = !port ? [0] : port.init_date.split('-');

    if (Number(date[0]) < 2008) {
      return false;
    }

    return `${date[1]}/${date[0]}`;
  };

  getNewPortDate = (initialDate) => {
    const date = new Date(initialDate);
    const today = new Date();
    const monthDifference =
      today.getMonth() -
      date.getMonth() +
      12 * (today.getFullYear() - date.getFullYear());
    if (monthDifference > 120) {
      return false;
    }

    const init = date.toDateString().toString().split(/\s+/);

    const months = {
      Jan: 'January',
      Feb: 'February',
      Mar: 'March',
      Apr: 'April',
      May: 'May',
      Jun: 'June',
      Jul: 'July',
      Aug: 'August',
      Sept: 'September',
      Oct: 'October',
      Nov: 'November',
      Dec: 'December',
    };

    return `${months[init[1]]} ${init[3]}`;
  };

  getParamsPort = () => {
    const { portfolios, portLongevityInputs } = this.props;
    const { portID } = portLongevityInputs;

    const portfolio = portfolios.find((item) => item.id === portID);

    return {
      symbols: portfolio.symbols,
      weights: portfolio.weights,
      region: portfolio.region,
      gic_info: portfolio.gic_info,
      init_date: portfolio.init_date,
    };
  };

  getParamsAcc = () => {
    const { portLongevityInputs, accountList } = this.props;
    const { portID } = portLongevityInputs;
    if (!accountList) {
      return {};
    }
    const account = accountList.find(
      (item) => Number(item.id) === Number(portID)
    );

    if (!account) {
      return {};
    }

    const accountInfo = account;
    const accToPort = convertToTickersAndWeights(account.holdings);
    const gicFormat = /^GIC\d*:MKT$/;
    const gicList = account.holdings.filter((holding) => {
      if (
        gicFormat.test(holding.security) ||
        gicFormat.test(
          holding.security_detail ? holding.security_detail.ticker : ''
        )
      ) {
        return true;
      }
      return false;
    });
    const gic_info = gicList.reduce((acc, gicKey) => {
      acc[gicKey.ticker] = {
        gic_name: gicKey.gic_name,
        gic_quantity: gicKey.quantity,
        gic_rate: gicKey.gic_rate,
        gic_ticker: gicKey.gic_ticker,
      };

      return acc;
    }, {});

    return {
      symbols: accToPort.tickers,
      weights: accToPort.weights,
      region: 'CA',
      gic_info,
      init_date: accountInfo.analytics_start_date,
    };
  };

  handleKeyPress = (e) => e.key === '.' && e.preventDefault();

  handlePortfolioChange = (e, { value }) => {
    const { dispatch } = this.props;
    dispatch(Planning.clearSectionData('savingResult'));

    return dispatch(
      CacheInputs.updateField('planning', 'portfolio_longevity', {
        portfolio: value,
        portID: value,
        errors: { portfolio: null },
      })
    );
  };

  handleHeaderChange = (value) => {
    const { dispatch, portLongevityInputs } = this.props;
    const { errors } = portLongevityInputs;

    dispatch(Planning.clearSectionData('longevity'));

    return dispatch(
      CacheInputs.updateField('planning', 'portfolio_longevity', {
        isPorts: value,
        portID: '',
        errors: { ...errors, portID: null },
      })
    );
  };

  handleChange = (e, { name, value }) => {
    const { dispatch } = this.props;

    if (name === 'gender') {
      return dispatch(
        CacheInputs.updateField('planning', 'portfolio_longevity', {
          gender: value,
          errors: { gender: null },
        })
      );
    }

    if (
      (name === 'retAge' || name === 'annuityRate' || name === 'lifeExp') &&
      Number(value) > 100
    ) {
      return dispatch(
        CacheInputs.updateField('planning', 'portfolio_longevity', {
          [name]: 100,
          errors: { [name]: null },
        })
      );
    }
    if (
      (name === 'lifeExp' || name === 'cf' || name === 'retAge') &&
      Number(value) < 0
    ) {
      return dispatch(
        CacheInputs.updateField('planning', 'portfolio_longevity', {
          [name]: 1,
          errors: { [name]: null },
        })
      );
    }

    return (
      value.length < 10 &&
      dispatch(
        CacheInputs.updateField('planning', 'portfolio_longevity', {
          [name]: value,
          errors: { [name]: null },
        })
      )
    );
  };

  scrollHandler = (element, to, duration) => {
    if (duration <= 0) return false;
    const difference = to - element.scrollTop;
    const perTick = (difference / duration) * 10;

    return setTimeout(() => {
      element.scrollTop += perTick;
      if (element.scrollTop === to) return false;
      return this.scrollHandler(element, to, duration - 10);
    }, 10);
  };

  hasEmptyValue = (values) =>
    values.some((field) => {
      const { dispatch, portLongevityInputs } = this.props;

      if (!portLongevityInputs[field]) {
        return dispatch(
          CacheInputs.updateField('planning', 'portfolio_longevity', {
            errors: { [field]: 'This field is required' },
          })
        );
      }

      return false;
    });

  handleSubmit = () => {
    const { dispatch, portLongevityInputs } = this.props;
    const {
      useDefault,
      lifeExp,
      portfolio,
      annuityRate,
      retAge,
      init,
      gender,
      cf,
      riskFree,
      portID,
      isPorts,
    } = portLongevityInputs;
    const params = {
      retAge,
      init,
      gender,
      cf,
      region: 'CA',
    };

    if (this.hasEmptyValue(Object.keys({ retAge, init, gender, cf }))) {
      return false;
    }

    if (!useDefault && !lifeExp) {
      return dispatch(
        CacheInputs.updateField('planning', 'portfolio_longevity', {
          errors: { lifeExp: 'This field is required' },
        })
      );
    }

    if (riskFree && !annuityRate) {
      return dispatch(
        CacheInputs.updateField('planning', 'portfolio_longevity', {
          errors: { annuityRate: 'This field is required' },
        })
      );
    }

    if (!riskFree && !portfolio) {
      return dispatch(
        CacheInputs.updateField('planning', 'portfolio_longevity', {
          errors: { portfolio: 'This field is required' },
        })
      );
    }

    if (annuityRate && riskFree) {
      params.annuityRate = annuityRate / 100;
    }

    if (lifeExp && !useDefault) {
      params.lifeExp = lifeExp;
    }

    if (portID && !riskFree) {
      const selectionParams = isPorts
        ? this.getParamsPort()
        : this.getParamsAcc();
      params.symbols = selectionParams.symbols;
      params.weights = selectionParams.weights;
      params.gic_info = selectionParams.gic_info;
      params.init_date = selectionParams.init_date;
    }

    return dispatch(Planning.analyzeLongevity(params)).then((action) => {
      const element = document.documentElement;
      const target = document.getElementById('longevity-ref').offsetHeight;

      return (
        action && !!action.result && this.scrollHandler(element, target, 300)
      );
    });
  };

  render() {
    const {
      dispatch,
      isFetching,
      longevity,
      portfolios,
      portLongevityInputs,
      user_region,
      accountData,
    } = this.props;
    const {
      lifeExp,
      useDefault,
      retAge,
      init,
      cf,
      gender,
      riskFree,
      annuityRate,
      errors,
      portID,
      isPorts,
    } = portLongevityInputs;

    const curPort = portfolios.find((port) => Number(port.id) === portID);

    const portDate =
      portID &&
      curPort &&
      (isPorts
        ? this.getNewPortDate(curPort.init_date)
        : accountData
        ? this.getNewPortDate(
            accountData.find((port) => Number(port.id) === portID)
              .analytics_start_date
          )
        : false);

    let textValue = '';

    if (portID && portID !== 0) {
      if (isPorts) {
        if (portfolios && portfolios.length > 0) {
          const match = curPort;
          if (match) {
            textValue = match.name || '';
          }
        }
      } else if (accountData && accountData.length > 0) {
        const match = accountData.find((p) => Number(p.id) === Number(portID));
        if (match) {
          textValue = match.label || '';
        }
      }
    }

    return (
      <Segment>
        <Header
          size="large"
          content="Portfolio Longevity"
          textAlign="center"
          subheader={
            <div className="sub header">
              You are retired. How long will your savings last? <br />
              Find out below if your savings are expected to outlive you!
            </div>
          }
        />

        <Form
          onSubmit={this.handleSubmit}
          style={{
            width: '80%',
            maxWidth: '800px',
            margin: '2rem auto 0 auto',
          }}
        >
          <Form.Group widths="equal">
            <Form.Field style={{ position: 'relative' }}>
              <label style={{ fontSize: '16px' }}>Age:</label>
              <Input
                name="retAge"
                type="number"
                fluid
                placeholder="years"
                value={retAge || ''}
                onChange={this.handleChange}
                onKeyPress={this.handleKeyPress}
              />
              <ErrorLabel className="top" msg={errors.retAge} />
            </Form.Field>

            <Form.Field style={{ position: 'relative' }}>
              <label style={{ fontSize: '16px' }}>
                Sex:{' '}
                <Popup
                  trigger={<Icon name="question circle outline" />}
                  position="right center"
                  style={{ height: 'initial' }}
                >
                  {user_region === 'CA'
                    ? 'We are using mortality rates from Statistics Canada in the calculations and the data is available for males and females only.'
                    : 'We are using mortality rates from the National Center for Health Statistics in the calculations and the data is available for males and females only.'}
                </Popup>
              </label>
              <Dropdown
                name="gender"
                compact
                placeholder="Sex"
                selection
                options={[
                  { key: 'm', value: 'M', text: 'Male' },
                  { key: 'f', value: 'F', text: 'Female' },
                ]}
                value={gender}
                onChange={this.handleChange}
              />
              <ErrorLabel className="top" msg={errors.gender} />
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field style={{ position: 'relative' }}>
              <label style={{ fontSize: '16px' }}>Retirement Savings:</label>
              <Input
                name="init"
                type="number"
                fluid
                value={init || ''}
                icon="dollar"
                iconPosition="left"
                onChange={this.handleChange}
                onKeyPress={this.handleKeyPress}
              />
              <ErrorLabel className="top" msg={errors.init} />
              <SubLabel>How much you currently have.</SubLabel>
            </Form.Field>

            <Form.Field style={{ position: 'relative' }}>
              <label style={{ fontSize: '16px' }}>Withdrawal per year:</label>
              <Input
                name="cf"
                type="number"
                fluid
                value={cf || ''}
                icon="dollar"
                iconPosition="left"
                onChange={this.handleChange}
                onKeyPress={this.handleKeyPress}
              />
              <ErrorLabel className="top" msg={errors.cf} />
              <SubLabel>
                How much you withdraw from your savings.
                <Popup
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{ fontSize: '13px', verticalAlign: 'initial' }}
                    />
                  }
                  position="right center"
                  content="This amount is what you spend per year over and above any retirement income you may receive, i.e., spending that has to be financed by your savings. We assume that this amount grows with inflation at 2% over time."
                />
              </SubLabel>
            </Form.Field>
          </Form.Group>
          <Divider />

          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Header
              size="tiny"
              textAlign="left"
              style={{ margin: 0, fontSize: '16px' }}
            >
              Life expectancy
              <Header.Subheader
                style={{ marginTop: '0.5rem', fontSize: '16px' }}
              >
                {user_region === 'CA'
                  ? 'Use Statistics Canada estimate based on my age and gender'
                  : 'Use National Center for Health Statistics based on my age and gender'}
              </Header.Subheader>
            </Header>

            <div style={{ textAlign: 'center', margin: '0' }}>
              <StyledSpan
                active={!!useDefault}
                onClick={() =>
                  dispatch(
                    CacheInputs.updateField('planning', 'portfolio_longevity', {
                      useDefault: true,
                    })
                  )
                }
              >
                Yes
              </StyledSpan>
              <StyledSpan
                active={!useDefault}
                onClick={() =>
                  dispatch(
                    CacheInputs.updateField('planning', 'portfolio_longevity', {
                      useDefault: false,
                    })
                  )
                }
              >
                No
              </StyledSpan>
            </div>
          </div>

          {!useDefault && (
            <Form.Field style={{ position: 'relative', margin: '1rem auto' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <label style={{ textAlign: 'left', fontSize: '16px' }}>
                  I expect to live for another
                </label>
                <div style={{ fontSize: '16px' }}>
                  <Input
                    size="mini"
                    type="number"
                    name="lifeExp"
                    label={{ basic: true, content: 'years' }}
                    labelPosition="right"
                    value={lifeExp || ''}
                    onChange={this.handleChange}
                    onKeyPress={this.handleKeyPress}
                    style={{ maxWidth: '5rem', marginRight: '3.5rem' }}
                  />
                  <ErrorLabel msg={errors.lifeExp} />
                </div>
              </div>
            </Form.Field>
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              margin: '2rem 0',
            }}
          >
            <Header
              size="tiny"
              content="Are you putting money into a risk-free investment?"
              textAlign="left"
              style={{ margin: '0', fontSize: '16px' }}
            />

            <div style={{ textAlign: 'center', margin: '0' }}>
              <StyledSpan
                active={!!riskFree}
                onClick={() =>
                  dispatch(
                    CacheInputs.updateField('planning', 'portfolio_longevity', {
                      riskFree: true,
                      portfolio: '',
                      portID: '',
                    })
                  )
                }
              >
                Yes
              </StyledSpan>
              <StyledSpan
                active={!riskFree}
                onClick={() =>
                  dispatch(
                    CacheInputs.updateField('planning', 'portfolio_longevity', {
                      riskFree: false,
                      annuityRate: '',
                    })
                  )
                }
              >
                No
              </StyledSpan>
            </div>
          </div>

          {!riskFree && (
            <Form.Field
              style={{
                position: 'relative',
                width: '100%',
                margin: '0 auto',
              }}
            >
              <label style={{ fontSize: '16px' }}>
                Choose the portfolio for your savings:
                <Popup
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{ fontSize: '16px', verticalAlign: 'initial' }}
                    />
                  }
                  position="right center"
                  wide
                  content="You can choose a model portfolio or any of your saved portfolios or accounts here. See the “Portfolios” segment."
                />
              </label>
              <div
                style={{
                  position: 'relative',
                  margin: '0 auto',
                }}
              >
                <PortfolioAccountSelector
                  value={`${portID}`}
                  text={textValue}
                  onChange={this.handlePortfolioChange}
                  isPorts={isPorts}
                  updateIsPorts={this.handleHeaderChange}
                />
              </div>
              <ErrorLabel msg={errors.portfolio} />
            </Form.Field>
          )}

          <Message
            icon
            visible={!!portDate}
            warning
            style={{
              margin: '1rem auto 0 auto',
              maxWidth: '45rem',
              textAlign: 'left',
            }}
          >
            <Icon name="exclamation" />
            <Message.Content>
              This portfolio has a start date of <strong>{portDate}</strong>.
              You may wish to use a portfolio with a longer history. You can use
              the ETF Portfolio Builder in the "Portfolios" segment. Once saved,
              the portfolio will appear in the dropdown list here.
            </Message.Content>
          </Message>

          {riskFree && (
            <Form.Field style={{ position: 'relative', margin: '0 auto' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <label style={{ fontSize: '16px' }}>
                  Enter the annual return:
                </label>
                <div style={{ width: '120px' }}>
                  <Input
                    type="number"
                    step="0.01"
                    name="annuityRate"
                    icon="percent"
                    value={annuityRate || ''}
                    onChange={this.handleChange}
                  />
                  <ErrorLabel msg={errors.annuityRate} />
                </div>
              </div>
            </Form.Field>
          )}

          <Button
            className={cn('centered right', { loading: isFetching })}
            color={colors.teal}
            icon="calculator"
          >
            Calculate
          </Button>
        </Form>

        <Result
          isFetching={isFetching}
          result={longevity}
          riskFree={riskFree}
          useDefault={useDefault}
        />
      </Segment>
    );
  }
}

Longevity.propTypes = {
  dispatch: PropTypes.func,
  isFetching: PropTypes.bool,
  portfolios: PropTypes.array,
  longevity: PropTypes.object,
  portLongevityInputs: PropTypes.object,
  accountData: PropTypes.array,
  accountList: PropTypes.array,
};

Longevity.defaultProps = {
  dispatch: () => false,
  portfolios: [],
  longevity: null,
  isFetching: false,
  portLongevityInputs: {},
  accountData: [],
  accountList: [],
};

const StyledSpan = styled.span`
  padding: 0.3rem 0.7rem;
  cursor: pointer;
  font-size: 14px;
  background-color: ${(props) => (props.active ? colors.teal : 'white')};
  border: ${(props) =>
    props.active ? '1px solid transparent' : '1px solid #dcdcdc'};
  user-select: none;

  &:first-child {
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
  }

  &:last-child {
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  &:hover {
    border: 1px solid ${colors.teal};
  }
`;

const SubLabel = styled.div`
  color: rgba(0, 0, 0, 0.5);
  line-height: 0.8rem;
  font-style: italic;
  font-weight: 100;
  font-size: 16px;
  margin-top: 1rem;
`;
